import { useState, useContext, useEffect, useMemo } from 'react'
import { PassContext } from '../pass-context'
import { flatten } from "flat"
import _ from "lodash"

import { Typography, TextField, Button, Tooltip } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'
import PopupShell from '../popup-shell'
import dataSelector from './data-selector'
import { validateRequiredVariableValue } from '/utils/variable-required'

const PopupDialogModel = ({ open, close, title, path, variable }) => {

  const { model, variables, setVariables, openVariablesManager, variablesCatalog=[], isManagedPassTemplate=false } = useContext(PassContext)

  const [newVariables, setNewVariables] = useState({})
  const [sourcePaths, setSourcePaths] = useState([])

  const catalogMap = useMemo(() => _.keyBy(variablesCatalog, "var_name"), [variablesCatalog])

  const dataChanged = useMemo(() => {
    if (open) {
      if (!_.isEqual(variables, newVariables)) return true
    }
    return false
  }, [variables, newVariables, open])
  const instanceRequiredValidation = useMemo(() => validateRequiredVariableValue({
    value: _.get(newVariables, variable),
    catalogEntry: catalogMap[variable],
    label: "Variable value",
    isManagedPassTemplate
  }), [catalogMap, isManagedPassTemplate, newVariables, variable])

  // Runs on "open" state change or if template / instance data change 
  // Ensures we have the current data rather than something left behind from previous form 
  useEffect(() => {
    try {
      if (open) {
        setNewVariables(_.cloneDeep(variables))
      }
    }
    catch (err) {
      throw err
    }
  }, [open, path])

  // Refresh our template / instance data source paths if instance data changes
  useEffect(() => {
    setSourcePaths(Object.keys(flatten(newVariables)).map(p => `{{${p}}}`).sort())
  }, [newVariables])


  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////


  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }

  const saveFormData = event => {
    event.preventDefault() // Prevents a "submit" which reloads the page

    if (!_.isEqual(newVariables, variables)) setVariables(newVariables)
    close()
  }


  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  try {
    return (
      <PopupShell open={open} onClose={handleClose}>
        <div className="flex flex-col p-2 h-full w-full overflow-x-hidden">

          <div className="flex flex-row justify-between mt-5">
            <Typography color="secondary" variant="h6">{title}</Typography>
            <Tooltip title="You can press ESC on your keyboard to dismiss"><Button endIcon={<EastRoundedIcon />} onClick={handleClose} >cancel</Button></Tooltip>
          </div>

          <hr />

          <div className='mt-5 overflow-y-scroll flex flex-col gap-5 pt-5'>
            <DataSourceInstance path={path} model={model} variables={newVariables} setNewVariables={setNewVariables} sourcePaths={sourcePaths} catalogMap={catalogMap} isManagedPassTemplate={isManagedPassTemplate} variable={variable} requiredValidation={instanceRequiredValidation} />
          </div>

          <div className='fixed bottom-5 w-[460px]'>
            <hr className='mb-5' />
            <div className="flex flex-row gap-5 justify-between">
              <Button startIcon={<DatasetOutlinedIcon />} onClick={openVariablesManager} >Manage variables</Button>
              <Button variant="contained" onClick={saveFormData} disabled={!dataChanged || !instanceRequiredValidation.valid}>Save</Button>
            </div>
          </div>

        </div>
      </PopupShell>
    )
  }
  catch (err) {
    throw err
  }
}

export default PopupDialogModel

const DataSourceInstance = ({ model, path, variables, setNewVariables, catalogMap, isManagedPassTemplate, variable, requiredValidation }) => {

  try {
    let modelValue = _.get(model, path)
    const fixedVariableValue = variable ? `{{${variable}}}` : null

    useEffect(() => {
      if (!fixedVariableValue) return
      if (_.get(modelValue, path) === fixedVariableValue) return
    }, [fixedVariableValue, path])

    if (typeof modelValue !== "undefined") return (
      <SimpleSelection active={Boolean(fixedVariableValue)} model={model} variables={variables} setNewVariables={setNewVariables} path={path} catalogMap={catalogMap} isManagedPassTemplate={isManagedPassTemplate} variable={variable} requiredValidation={requiredValidation} />
    )
  }
  catch (err) {
    console.error(err)
  }
}


const SimpleSelection = ({ active, model, variables, setNewVariables, path, catalogMap, isManagedPassTemplate, variable, requiredValidation }) => {

  const modelValue = _.get(model, path)
  const variablesValue = dataSelector({ template: model, variables, path })
  const variablesPath = variable ?? (typeof modelValue === "string" ? (modelValue.match(/^{{(.+)}}$/)?.[1] ?? "") : "")
  const catalogEntry = catalogMap[variablesPath]
  const instanceValueReadonly = catalogEntry?.editable !== undefined && Number(catalogEntry?.editable) === 0

  const changeInstanceValue = value => {
    if (!variablesPath || instanceValueReadonly) return

    setNewVariables(prev => {
      let nd = _.cloneDeep(prev)
      _.set(nd, variablesPath, value)
      return nd
    })
  }

  if (active && typeof variablesValue !== "undefined") return (
    <>
      <TextField
        label="Variable name"
        value={variable}
        slotProps={{ inputLabel: { shrink: true } }}
        disabled={true}
        fullWidth
      />

      <TextField label="Variable value"
        value={variablesValue}
        slotProps={{ inputLabel: { shrink: true } }}
        type="search"
        disabled={instanceValueReadonly}
        fullWidth
        error={!requiredValidation.valid}
        helperText={requiredValidation.helperText || undefined}
        onChange={e => changeInstanceValue(e.target.value)}
      />
    </>
  )
}
